import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  VStack,
  HStack,
  Text,
  Box,
  Avatar,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Spinner,
  Button,
  Icon,
  Divider,
} from '@chakra-ui/react';
import { FaSearch, FaTimes, FaCheckCircle } from 'react-icons/fa';
import apiClient from '../lib/api/apiClient';
import { ROSTER_SECTIONS_CONFIG } from '../constants';
import { httpsPlayerImageUrl } from '../utils/stringUtils';
import { formatPositionsList } from '../utils/formatPositionsList';
import { getPstTimeDifference } from '../lib/pstTime';

interface Player {
  id: number;
  primaryPosition?: string[];
  secondaryPosition?: string[];
  tertiaryPosition?: string[];
  rwPlayer: {
    id: number;
    first_name: string;
    last_name: string;
    position_1: string;
    position_2?: string;
    position_3?: string;
    seasonPositions?: {
      primaryPosition?: string[] | null;
      secondaryPosition?: string[] | null;
      tertiaryPosition?: string[] | null;
    } | null;
    mlb_team?: string;
    image?: string;
    bats?: string;
    throws?: string;
  };
  playerContract?: {
    current_salary: number;
    contractYears?: { year: number; salary: number }[];
  };
  contract?: {
    current_salary: number;
    contractYears?: { year: number; salary: number }[];
  };
  status?: string;
  status_until?: string | null;
  level?: string;
  is_40_man?: boolean;
  team_id?: number;
  year?: number;
  deal_status?: string | null;
  deal_until?: string | null;
  dealData?: unknown;
}

/** POS line from season buckets, same as roster tables. */
function formatPlayerPositionsLine(player: Player): string {
  const sp = player.rwPlayer?.seasonPositions;
  return formatPositionsList({
    primaryPosition: (player.primaryPosition ?? sp?.primaryPosition) ?? undefined,
    secondaryPosition: (player.secondaryPosition ?? sp?.secondaryPosition) ?? undefined,
    tertiaryPosition: (player.tertiaryPosition ?? sp?.tertiaryPosition) ?? undefined,
  });
}

interface ILPlayerInfo {
  id: number;
  rwPlayer: {
    first_name: string;
    last_name: string;
    image?: string;
    position_1?: string;
  };
  playerContract?: {
    current_salary: number;
  };
}

interface ILReplacementModalProps {
  isOpen: boolean;
  onClose: () => void;
  ilPlayer: ILPlayerInfo;
  teamId: number;
  fortyManRosterSize: number | null;
  onConfirm: (replacementPlayer: Player) => void;
  isProcessing?: boolean;
}

const ILReplacementModal: React.FC<ILReplacementModalProps> = ({
  isOpen,
  onClose,
  ilPlayer,
  teamId,
  fortyManRosterSize,
  onConfirm,
  isProcessing = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [availablePlayers, setAvailablePlayers] = useState<Player[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  // Step 1 = selection, Step 2 = confirmation
  const [step, setStep] = useState<1 | 2>(1);

  const firstName = ilPlayer.rwPlayer.first_name;
  const lastName = ilPlayer.rwPlayer.last_name;

  // Load available players from the full roster
  const loadAvailablePlayers = async () => {
    if (!teamId) return;

    setIsLoading(true);
    try {
      const response = await apiClient.get(`/roster/full-roster/${teamId}`);
      const players: Player[] = response.data?.players || [];

      // Filter to only players belonging to this team
      const teamPlayers = players.filter((player: Player) => {
        return player.team_id === teamId;
      });

      setAvailablePlayers(teamPlayers);
    } catch (error) {
      console.error('Error loading available players:', error);
      setAvailablePlayers([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && teamId) {
      loadAvailablePlayers();
      setSearchTerm('');
      setSelectedPlayer(null);
      setStep(1);
    }
  }, [isOpen, teamId]);

  /**
   * Filter players to those eligible as IL replacements:
   * 1. Players in Awaiting Assignment
   * 2. Players on 10 Day IL or 60 Day IL that are available for recall (status_until passed)
   * 3. ALL minor league players (bypassing option hold for IL moves)
   *
   * Exclude the player being moved to IL, and exclude players with
   * restricted deal statuses (waivers, claims, trades, etc.)
   */
  const eligiblePlayers = useMemo(() => {
    const AAA_ORDER = ROSTER_SECTIONS_CONFIG.find(sec => sec.label === 'AAA')?.order ?? 11;

    return availablePlayers.filter((player) => {
      // Exclude the player being moved to IL
      if (player.id === ilPlayer.id) return false;

      // Exclude players with restricted deal statuses
      const dealStatus = (player.deal_status || '').toLowerCase();
      if (
        dealStatus.includes('waiv') ||
        dealStatus.includes('claim') ||
        dealStatus.includes('free_agent') ||
        dealStatus.includes('review') ||
        dealStatus.includes('settlement') ||
        player.deal_status === 'franchise_tag' ||
        player.deal_status === 'trade'
      ) {
        return false;
      }

      // 1. Awaiting Assignment players
      if (
        player.status === 'Awaiting Assignment' ||
        (player.deal_status && player.deal_status.toLowerCase().includes('awaiting_assignment'))
      ) {
        return true;
      }

      // 2. Players on 10 Day IL or 60 Day IL that are available for recall
      const statusLower = (player.status || '').toLowerCase();
      const is10DayIL =
        statusLower === 'dl10' ||
        statusLower === '10-day dl' ||
        statusLower === '10 day dl' ||
        statusLower === '10-day il' ||
        statusLower === '10 day il';
      const is60DayIL =
        statusLower === 'dl60' ||
        statusLower === '60-day dl' ||
        statusLower === '60 day dl' ||
        statusLower === '60-day il' ||
        statusLower === '60 day il';

      if (is10DayIL || is60DayIL) {
        // Check if status_until has passed (available for recall)
        const statusUntilPassed =
          player.status_until == null ||
          getPstTimeDifference(player.status_until) <= 0;
        return statusUntilPassed;
      }

      // 3. All minor league players (bypass option hold)
      const playerLevel = (player.level || '').toLowerCase();
      const isMinorLeague = ROSTER_SECTIONS_CONFIG.some(
        (section) => {
          if (section.order < AAA_ORDER) return false; // Skip MLB sections
          if (section.level.toLowerCase() === playerLevel) return true;
          if ((section as any).levelAliases) {
            return (section as any).levelAliases.some(
              (alias: string) => alias.toLowerCase() === playerLevel
            );
          }
          return false;
        }
      );

      return isMinorLeague;
    });
  }, [availablePlayers, ilPlayer.id]);

  // Apply search filter
  const filteredPlayers = useMemo(() => {
    if (!searchTerm) return eligiblePlayers;

    return eligiblePlayers.filter((player) => {
      const fullName = `${player.rwPlayer.first_name} ${player.rwPlayer.last_name}`.toLowerCase();
      const posLine = formatPlayerPositionsLine(player);
      const posForSearch =
        posLine === '—'
          ? ''
          : posLine.replace(/\//g, ' ').replace(/,/g, ' ').toUpperCase();

      const searchLower = searchTerm.toLowerCase();
      const searchUpper = searchTerm.toUpperCase();
      return fullName.includes(searchLower) || posForSearch.includes(searchUpper);
    });
  }, [eligiblePlayers, searchTerm]);

  // Group players by section
  const getPlayerSection = (player: Player): string => {
    const AAA_ORDER = ROSTER_SECTIONS_CONFIG.find(sec => sec.label === 'AAA')?.order ?? 11;

    if (
      player.status === 'Awaiting Assignment' ||
      (player.deal_status && player.deal_status.toLowerCase().includes('awaiting_assignment'))
    ) {
      return 'Awaiting Assignment';
    }

    const statusLower = (player.status || '').toLowerCase();
    // Check if IL
    const is10DayIL =
      statusLower === 'dl10' || statusLower === '10-day dl' || statusLower === '10 day dl' ||
      statusLower === '10-day il' || statusLower === '10 day il';
    const is60DayIL =
      statusLower === 'dl60' || statusLower === '60-day dl' || statusLower === '60 day dl' ||
      statusLower === '60-day il' || statusLower === '60 day il';

    if (is10DayIL) return '10 Day Injured List';
    if (is60DayIL) return '60 Day Injured List';

    // Match minor league sections
    for (const section of ROSTER_SECTIONS_CONFIG) {
      if (section.order < AAA_ORDER) continue;
      const playerLevel = (player.level || '').toLowerCase();
      if (section.level.toLowerCase() === playerLevel) return section.label;
      if ((section as any).levelAliases) {
        const aliasMatch = (section as any).levelAliases.some(
          (alias: string) => alias.toLowerCase() === playerLevel
        );
        if (aliasMatch) return section.label;
      }
    }

    return 'Other';
  };

  const playersBySection = useMemo((): Array<[string, Player[]]> => {
    const grouped: { [sectionLabel: string]: Player[] } = {};

    filteredPlayers.forEach((player) => {
      const sectionLabel = getPlayerSection(player);
      if (!grouped[sectionLabel]) {
        grouped[sectionLabel] = [];
      }
      grouped[sectionLabel].push(player);
    });

    // Define section order
    const sectionOrder = [
      'Awaiting Assignment',
      '10 Day Injured List',
      '60 Day Injured List',
      ...ROSTER_SECTIONS_CONFIG
        .filter(sec => (sec.order ?? 0) >= (ROSTER_SECTIONS_CONFIG.find(s => s.label === 'AAA')?.order ?? 11))
        .sort((a, b) => a.order - b.order)
        .map(sec => sec.label),
      'Other',
    ];

    const sortedSections: Array<[string, Player[]]> = [];
    sectionOrder.forEach((label) => {
      if (grouped[label] && grouped[label].length > 0) {
        // Sort players within section by last name
        grouped[label].sort((a, b) => {
          const lastA = a.rwPlayer.last_name || '';
          const lastB = b.rwPlayer.last_name || '';
          return lastA.localeCompare(lastB);
        });
        sortedSections.push([label, grouped[label]]);
      }
    });

    return sortedSections;
  }, [filteredPlayers]);

  const handlePlayerSelect = (player: Player) => {
    if (selectedPlayer?.id === player.id) {
      setSelectedPlayer(null);
    } else {
      setSelectedPlayer(player);
    }
  };

  const handleConfirmClick = () => {
    if (step === 1 && selectedPlayer) {
      setStep(2);
    } else if (step === 2 && selectedPlayer) {
      onConfirm(selectedPlayer);
    }
  };

  const handleBack = () => {
    setStep(1);
  };

  // Check if selected player needs 40-man spot
  const needs40ManSpot = selectedPlayer && !selectedPlayer.is_40_man;
  const fortyManFull = (fortyManRosterSize ?? 0) >= 40;
  const is40ManBlocked = needs40ManSpot && fortyManFull;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      isCentered
      closeOnOverlayClick={false}
      closeOnEsc={!isProcessing}
    >
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent
        maxW="lg"
        mx={4}
        borderRadius="xl"
        boxShadow="2xl"
        bg="gray.800"
        color="gray.100"
      >
        <ModalHeader pb={2} pt={6}>
          <Text fontSize="xl" fontWeight="bold" color="white" lineHeight="1.2">
            Move {lastName} to 10 Day IL
          </Text>
        </ModalHeader>
        <ModalCloseButton color="gray.400" isDisabled={isProcessing} />

        <ModalBody pt={0} pb={4} px={6}>
          {step === 1 ? (
            <VStack spacing={4} align="stretch">
              {/* Descriptive text */}
              <Text color="gray.200" lineHeight="1.5" fontSize="md">
                In order to move {firstName} {lastName} to the 10 Day IL list, you must select a replacement. You will still be responsible for {firstName} {lastName}'s salary.
              </Text>

              <Divider borderColor="gray.600" />

              {/* Subsection title */}
              <Text fontWeight="bold" fontSize="md" color="gray.100">
                Select player to replace {lastName}:
              </Text>

              {/* Search Input */}
              <Box w="100%">
                <InputGroup size="sm">
                  <InputLeftElement>
                    <Icon as={FaSearch} color="gray.500" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search players by name or position..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    bg="gray.700"
                    borderColor="gray.600"
                    color="gray.100"
                    _focus={{ borderColor: 'blue.400' }}
                    _placeholder={{ color: 'gray.500' }}
                  />
                  {searchTerm && (
                    <InputRightElement>
                      <Icon
                        as={FaTimes}
                        color="gray.500"
                        cursor="pointer"
                        _hover={{ color: 'gray.300' }}
                        onClick={() => setSearchTerm('')}
                        boxSize={3}
                      />
                    </InputRightElement>
                  )}
                </InputGroup>
              </Box>

              {/* Players List */}
              <Box
                w="100%"
                maxH="350px"
                overflowY="auto"
                border="1px solid"
                borderColor="gray.600"
                borderRadius="md"
                css={{
                  '&::-webkit-scrollbar': { width: '6px' },
                  '&::-webkit-scrollbar-track': { background: 'transparent' },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#4A5568',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': { background: '#718096' },
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#4A5568 transparent',
                }}
              >
                {isLoading ? (
                  <VStack justify="center" py={8}>
                    <Spinner size="lg" color="blue.400" />
                    <Text color="gray.500">Loading players...</Text>
                  </VStack>
                ) : filteredPlayers.length === 0 ? (
                  <VStack justify="center" py={8}>
                    <Text color="gray.500">No eligible players found</Text>
                  </VStack>
                ) : (
                  <VStack spacing={3} p={3} align="stretch">
                    {playersBySection.map(([sectionLabel, players]) => (
                      <Box key={sectionLabel}>
                        <Text
                          fontWeight="bold"
                          color="gray.300"
                          mb={2}
                          fontSize="sm"
                          position="sticky"
                          top={0}
                          bg="gray.800"
                          py={1}
                          zIndex={1}
                          textTransform="uppercase"
                          letterSpacing="0.5px"
                        >
                          {sectionLabel}
                        </Text>
                        <VStack spacing={1} align="stretch">
                          {players.map((player) => {
                            const positions = formatPlayerPositionsLine(player);
                            const salary =
                              player.playerContract?.current_salary ||
                              player.contract?.current_salary ||
                              0;
                            const formattedSalary = salary
                              ? `$${(salary / 1000000).toFixed(2)}M`
                              : 'Min';
                            const isSelected = selectedPlayer?.id === player.id;

                            // Check if 40-man would be blocked for this player
                            const playerNeeds40Man = !player.is_40_man;
                            const playerBlocked = playerNeeds40Man && fortyManFull;

                            return (
                              <Box
                                key={player.id}
                                p={2}
                                border="1px solid"
                                borderColor={isSelected ? 'blue.400' : 'gray.600'}
                                borderRadius="md"
                                cursor={playerBlocked ? 'not-allowed' : 'pointer'}
                                opacity={playerBlocked ? 0.5 : 1}
                                bg={isSelected ? 'rgba(66, 153, 225, 0.15)' : 'transparent'}
                                _hover={
                                  playerBlocked
                                    ? {}
                                    : { bg: 'gray.700' }
                                }
                                onClick={() => {
                                  if (!playerBlocked) {
                                    handlePlayerSelect(player);
                                  }
                                }}
                                transition="all 0.15s"
                              >
                                <HStack spacing={3}>
                                  {httpsPlayerImageUrl(player.rwPlayer.image) ? (
                                    <Box
                                      w="32px"
                                      h="32px"
                                      borderRadius="full"
                                      overflow="hidden"
                                      bg="transparent"
                                      flexShrink={0}
                                    >
                                      <img
                                        src={httpsPlayerImageUrl(player.rwPlayer.image)}
                                        alt={`${player.rwPlayer.first_name} ${player.rwPlayer.last_name}`}
                                        style={{
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'cover',
                                        }}
                                        onError={(e) => {
                                          e.currentTarget.style.display = 'none';
                                        }}
                                      />
                                    </Box>
                                  ) : (
                                    <Avatar
                                      size="sm"
                                      name={`${player.rwPlayer.first_name} ${player.rwPlayer.last_name}`}
                                      bg="gray.600"
                                      color="white"
                                    />
                                  )}
                                  <Box flex="1">
                                    <Text
                                      fontWeight="semibold"
                                      color="gray.100"
                                      fontSize="sm"
                                    >
                                      {player.rwPlayer.first_name}{' '}
                                      {player.rwPlayer.last_name}
                                    </Text>
                                    <Text fontSize="xs" color="gray.400">
                                      {positions} • {formattedSalary}
                                    </Text>
                                  </Box>
                                  {player.is_40_man && (
                                    <Badge
                                      colorScheme="blue"
                                      size="sm"
                                      fontSize="10px"
                                    >
                                      40-MAN
                                    </Badge>
                                  )}
                                  {playerBlocked && (
                                    <Badge
                                      colorScheme="red"
                                      size="sm"
                                      fontSize="10px"
                                      title="40-man roster is full"
                                    >
                                      40-Man Full
                                    </Badge>
                                  )}
                                  {isSelected && (
                                    <Icon
                                      as={FaCheckCircle}
                                      color="blue.400"
                                      boxSize={4}
                                    />
                                  )}
                                </HStack>
                              </Box>
                            );
                          })}
                        </VStack>
                      </Box>
                    ))}
                  </VStack>
                )}
              </Box>

              {/* 40-man warning */}
              {is40ManBlocked && selectedPlayer && (
                <Text fontSize="sm" color="red.300">
                  40-man roster is full. You must make room before selecting a
                  player not currently on your 40-man roster.
                </Text>
              )}
            </VStack>
          ) : (
            /* Step 2: Confirmation */
            <VStack spacing={4} align="stretch">
              <Box
                bg="gray.700"
                p={4}
                borderRadius="md"
                border="1px solid"
                borderColor="gray.600"
              >
                <Text color="gray.300" fontSize="sm" fontWeight="bold" mb={2} textTransform="uppercase" letterSpacing="0.5px">
                  Moving to 10 Day IL
                </Text>
                <HStack spacing={3}>
                  {httpsPlayerImageUrl(ilPlayer.rwPlayer.image) ? (
                    <Box w="32px" h="32px" borderRadius="full" overflow="hidden" bg="transparent" flexShrink={0}>
                      <img
                        src={httpsPlayerImageUrl(ilPlayer.rwPlayer.image)}
                        alt={`${firstName} ${lastName}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    </Box>
                  ) : (
                    <Avatar size="sm" name={`${firstName} ${lastName}`} bg="gray.600" color="white" />
                  )}
                  <Box>
                    <Text fontWeight="semibold" color="gray.100">{firstName} {lastName}</Text>
                    <Text fontSize="xs" color="gray.400">
                      → 10 Day Injured List
                    </Text>
                  </Box>
                </HStack>
              </Box>

              <Box
                bg="gray.700"
                p={4}
                borderRadius="md"
                border="1px solid"
                borderColor="green.600"
              >
                <Text color="green.300" fontSize="sm" fontWeight="bold" mb={2} textTransform="uppercase" letterSpacing="0.5px">
                  Adding to Active Roster
                </Text>
                {selectedPlayer && (
                  <HStack spacing={3}>
                    {httpsPlayerImageUrl(selectedPlayer.rwPlayer.image) ? (
                      <Box w="32px" h="32px" borderRadius="full" overflow="hidden" bg="transparent" flexShrink={0}>
                        <img
                          src={httpsPlayerImageUrl(selectedPlayer.rwPlayer.image)}
                          alt={`${selectedPlayer.rwPlayer.first_name} ${selectedPlayer.rwPlayer.last_name}`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      </Box>
                    ) : (
                      <Avatar
                        size="sm"
                        name={`${selectedPlayer.rwPlayer.first_name} ${selectedPlayer.rwPlayer.last_name}`}
                        bg="gray.600"
                        color="white"
                      />
                    )}
                    <Box>
                      <HStack spacing={2}>
                        <Text fontWeight="semibold" color="gray.100">
                          {selectedPlayer.rwPlayer.first_name} {selectedPlayer.rwPlayer.last_name}
                        </Text>
                        {selectedPlayer.is_40_man && (
                          <Badge colorScheme="blue" size="sm" fontSize="10px">40-MAN</Badge>
                        )}
                      </HStack>
                      <Text fontSize="xs" color="gray.400">
                        {formatPlayerPositionsLine(selectedPlayer)} • → Active Roster
                      </Text>
                    </Box>
                  </HStack>
                )}
              </Box>

              <Text color="gray.400" fontSize="sm">
                You will still be responsible for {firstName} {lastName}'s salary.
              </Text>
            </VStack>
          )}
        </ModalBody>

        <ModalFooter pt={0} pb={6} px={6}>
          <HStack spacing={4} w="full" justify="flex-end">
            {step === 2 && (
              <Button
                variant="outline"
                colorScheme="gray"
                onClick={handleBack}
                size="md"
                fontWeight="semibold"
                borderWidth="2px"
                isDisabled={isProcessing}
                _hover={{
                  bg: 'gray.700',
                  borderColor: 'gray.500',
                }}
              >
                Back
              </Button>
            )}
            <Button
              variant="outline"
              colorScheme="gray"
              onClick={onClose}
              size="md"
              fontWeight="semibold"
              borderWidth="2px"
              isDisabled={isProcessing}
              _hover={{
                bg: 'gray.700',
                borderColor: 'gray.500',
              }}
            >
              Cancel
            </Button>
            <Button
              colorScheme="green"
              onClick={handleConfirmClick}
              size="md"
              fontWeight="semibold"
              isDisabled={!selectedPlayer || is40ManBlocked || isProcessing}
              isLoading={isProcessing}
              loadingText="Processing..."
              _hover={{
                transform: 'translateY(-1px)',
                boxShadow: 'lg',
              }}
              transition="all 0.2s"
            >
              {step === 1 ? 'Move to 10 Day IL' : 'Confirm Moves'}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ILReplacementModal;
